import { useDateValidationProps } from "@judo/core";
import { useModelLabel } from "@judo/i18n";
import type { DateInput, Input } from "@judo/model-api";
import { getInputTestId } from "@judo/test-ids";
import type { TextFieldProps } from "@mui/material/TextField";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { isValid, parse, parseISO } from "date-fns";
import { buildIconAdornment } from "../utils/icon-adornment";

const DATE_ONLY_RE = /^\d{4}-\d{2}-\d{2}$/;

export interface DateInputComponentProps {
	element: DateInput | Input;
	readOnly?: boolean;
	value?: Date | string | null;
	onChange?: (value: Date | null) => void;
	error?: string;
	disabled?: boolean;
	judoClassName?: string;
}

/**
 * Parse a string value to a Date.
 *
 * `YYYY-MM-DD` strings are parsed via `date-fns` `parse` with a local
 * reference date so the resulting `Date` carries the literal year/month/day
 * in the runtime's local time zone (avoids the UTC-midnight shift that
 * `new Date("YYYY-MM-DD")` and `parseISO` would introduce).
 *
 * Other strings are parsed as ISO 8601 via `parseISO`.
 */
function parseToDate(value: Date | string | null | undefined): Date | null {
	if (value == null) return null;
	if (value instanceof Date) return isValid(value) ? value : null;
	if (DATE_ONLY_RE.test(value)) {
		const parsed = parse(value, "yyyy-MM-dd", new Date());
		return isValid(parsed) ? parsed : null;
	}
	const parsed = parseISO(value);
	return isValid(parsed) ? parsed : null;
}

/**
 * Date input component using MUI X DatePicker.
 */
export function DateInputComponent({
	element,
	readOnly,
	value,
	onChange,
	error,
	disabled,
	judoClassName,
}: DateInputComponentProps) {
	const testId = getInputTestId(element);
	const { label } = useModelLabel(element);
	const dateValidation = useDateValidationProps(element["xmi:id"]);
	const startAdornment = buildIconAdornment(element.icon);

	// Resolve min/max from DateValidationProps — explicit precedence:
	// an explicit min/maxDate always wins over disablePast/disableFuture
	// (so a caller can allow past dates above a floor, or vice versa).
	const today = new Date();
	const minDate = dateValidation?.minDate ?? (dateValidation?.disablePast ? today : undefined);
	const maxDate = dateValidation?.maxDate ?? (dateValidation?.disableFuture ? today : undefined);

	return (
		<DatePicker
			label={label}
			views={["year", "month", "day"]}
			className={judoClassName}
			value={parseToDate(value)}
			onChange={(newValue) => onChange?.(newValue)}
			disabled={disabled ?? element.disabled}
			readOnly={readOnly ?? element.isReadOnly}
			minDate={minDate}
			maxDate={maxDate}
			slotProps={{
				// Cast: MUI X's slot type strips arbitrary attributes, but `data-testid`
				// is a valid HTML attribute on the underlying TextField root.
				textField: {
					"data-testid": testId,
					error: !!error,
					helperText: error,
					required: element.required,
					fullWidth: true,
					slotProps: {
						input: {
							startAdornment,
						},
						inputLabel: {
							shrink: true,
						},
					},
				} as TextFieldProps,
				actionBar: {
					actions: ["today", "accept"],
				},
			}}
		/>
	);
}
